import {
  SPECTATOR_SEAT,
  type HistoryEntry,
  type Lobby,
  type Replay,
  type ReplaySummary,
  type ServerMessage,
} from '@archon3d/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { webSocketUrl } from '../config';
import { ReplayPlayer } from '../game/replay';
import type { GameSession, RoomLink } from '../game/session';
import { GameStarts } from './gameStarts';
import { keepDefinition } from './lobby';
import { RoomClient, isFatal, type CreateRoomOptions } from './roomClient';

/** Everything the app needs to put an online game (or a replay of one) on the table. */
export interface StartedGame {
  link: RoomLink;
  /** The local seat, or `SPECTATOR_SEAT` when only watching. */
  seat: number;
  started: Extract<ServerMessage, { t: 'started' }>;
  history: HistoryEntry[];
  battle?: Extract<ServerMessage, { t: 'sync' }>['battle'];
  /** Seats the AI stands in for, the local one among them when its person is back after a reload. */
  standIns?: number[];
  /** Seats whose people had dropped, with the seconds before the AI stands in for them. */
  paused?: { seat: number; secondsLeft: number }[];
  /** Set for a replay: the player that feeds the session paces itself by what it shows. */
  onSession?: (session: GameSession) => void;
}

export type RoomUiState =
  | { phase: 'idle'; error: string | null }
  | { phase: 'busy'; action: 'create' | 'join' | 'resume' }
  | {
      phase: 'lobby';
      code: string;
      seat: number;
      lobby: Lobby;
      host: boolean;
      error: string | null;
    }
  | { phase: 'playing'; code: string; seat: number }
  | { phase: 'replay'; summary: ReplaySummary };

export interface RoomController {
  state: RoomUiState;
  create(setup: CreateRoomOptions): void;
  /** `watch` asks for no seat; otherwise a seat is taken when one is free. */
  join(code: string, name: string, watch?: boolean): void;
  start(): void;
  /** Leaves the room, or stops the replay, and drops the connection. */
  leave(): void;
  /** The finished games the server remembers. */
  listReplays(): Promise<ReplaySummary[]>;
  fetchReplay(id: string): Promise<Replay>;
  /** Puts a finished game on the table; the banner carries the playback controls. */
  watchReplay(replay: Replay): void;
  /** The replay on the table, while one plays. */
  replay: ReplayPlayer | null;
}

interface Options {
  serverUrl: string;
  /** The room's game has started (or was found again after a reload): play it. */
  onStarted(game: StartedGame): void;
  /** A reloaded page found its lobby again: the menu shows it, as the host or a guest. */
  onLobbyFound?(host: boolean): void;
}

/**
 * Hosting and joining from the menu, on top of one `RoomClient` for the visit. Once the game
 * starts the session takes the client's events; the hook only remembers that a game is on.
 * A replay goes onto the table the same way, with a `ReplayPlayer` standing in for the server.
 */
export function useRoom({ serverUrl, onStarted, onLobbyFound }: Options): RoomController {
  const [state, setState] = useState<RoomUiState>({ phase: 'idle', error: null });
  const stateRef = useRef(state);
  stateRef.current = state;
  const [replay, setReplay] = useState<ReplayPlayer | null>(null);
  const clientRef = useRef<RoomClient | null>(null);
  const replayRef = useRef<ReplayPlayer | null>(null);
  const onStartedRef = useRef(onStarted);
  onStartedRef.current = onStarted;
  const onLobbyFoundRef = useRef(onLobbyFound);
  onLobbyFoundRef.current = onLobbyFound;
  /**
   * A `started` waits for its `sync` so the game is built from both at once; the pair a resume in
   * this tab re-sends is the running session's to apply, not a new game.
   */
  const starts = useRef(new GameStarts());

  const stopReplay = useCallback(() => {
    replayRef.current?.dispose();
    replayRef.current = null;
    setReplay(null);
  }, []);

  /** A resume from a page load landed in a lobby: back into it, on screen. */
  const lobbyFound = useCallback((room: { code: string; seat: number; lobby: Lobby }) => {
    const host = room.lobby.members.some((m) => m.seat === room.seat && m.host);
    setState({
      phase: 'lobby',
      code: room.code,
      seat: room.seat,
      lobby: room.lobby,
      host,
      error: null,
    });
    onLobbyFoundRef.current?.(host);
  }, []);

  const client = useCallback((): RoomClient => {
    if (clientRef.current) return clientRef.current;
    const created = new RoomClient(webSocketUrl(serverUrl));
    clientRef.current = created;
    created.subscribe((event) => {
      switch (event.t) {
        // A resume that got through on a later try: back into the lobby it was waiting in. (A game
        // under way comes back through its `started` and `sync`.)
        case 'joined': {
          const now = stateRef.current;
          if (event.lobby.started) return;
          if (now.phase === 'busy' && now.action === 'resume') lobbyFound(event);
          // Back after a drop in the lobby: whatever changed meanwhile, since nobody re-sends it.
          else
            setState((s) =>
              s.phase === 'lobby' && s.code === event.code
                ? {
                    ...s,
                    lobby: event.lobby,
                    host: event.lobby.members.some((m) => m.seat === event.seat && m.host),
                  }
                : s,
            );
          return;
        }
        case 'lobby':
          setState((s) =>
            s.phase === 'lobby'
              ? {
                  ...s,
                  lobby: keepDefinition(s.lobby, event.lobby),
                  host: event.lobby.members.some((m) => m.seat === s.seat && m.host),
                }
              : s,
          );
          return;
        case 'started':
          starts.current.started(event, created.membership?.code);
          return;
        case 'sync': {
          const game = starts.current.sync(event, created.membership?.code);
          if (!game) return;
          setState({ phase: 'playing', code: game.code, seat: created.seat });
          onStartedRef.current({
            link: created,
            seat: created.seat,
            started: game.started,
            history: game.sync.history,
            battle: game.sync.battle,
            standIns: game.sync.standIns,
            paused: game.sync.paused,
          });
          return;
        }
        case 'ended':
          // Said as it happened: a lobby left alone for half an hour expires, it was not closed
          // by anyone (net-server:quality#15).
          setState((s) =>
            s.phase === 'lobby'
              ? {
                  phase: 'idle',
                  error:
                    event.reason === 'expired'
                      ? 'The room closed after 30 minutes with no one in it'
                      : event.reason === 'abandoned'
                        ? 'Everyone else left the room'
                        : 'The host closed the room',
                }
              : s,
          );
          return;
        case 'error':
          setState((s) => (s.phase === 'lobby' ? { ...s, error: event.message } : s));
          return;
        case 'connection':
          if (event.state === 'closed')
            setState((s) =>
              s.phase === 'lobby' || s.phase === 'busy'
                ? { phase: 'idle', error: event.detail ?? 'The connection was lost' }
                : s,
            );
          return;
        default:
          return;
      }
    });
    return created;
  }, [serverUrl, lobbyFound]);

  const fail = (err: unknown) =>
    setState({ phase: 'idle', error: err instanceof Error ? err.message : String(err) });

  const create = useCallback<RoomController['create']>(
    (setup) => {
      stopReplay();
      starts.current.reset();
      setState({ phase: 'busy', action: 'create' });
      client()
        .create(setup)
        .then((room) =>
          setState({
            phase: 'lobby',
            code: room.code,
            seat: room.seat,
            lobby: room.lobby,
            host: true,
            error: null,
          }),
        )
        .catch(fail);
    },
    [client, stopReplay],
  );

  const join = useCallback<RoomController['join']>(
    (code, name, watch = false) => {
      stopReplay();
      starts.current.reset();
      setState({ phase: 'busy', action: 'join' });
      client()
        .join(code, name, watch)
        .then((room) =>
          setState({
            phase: 'lobby',
            code: room.code,
            seat: room.seat,
            lobby: room.lobby,
            host: false,
            error: null,
          }),
        )
        .catch(fail);
    },
    [client, stopReplay],
  );

  const start = useCallback(() => client().start(), [client]);
  const leave = useCallback(() => {
    stopReplay();
    clientRef.current?.leave();
    clientRef.current = null;
    starts.current.reset();
    setState({ phase: 'idle', error: null });
  }, [stopReplay]);

  const listReplays = useCallback(() => client().listReplays(), [client]);
  const fetchReplay = useCallback((id: string) => client().fetchReplay(id), [client]);
  const watchReplay = useCallback<RoomController['watchReplay']>(
    (game) => {
      // A replay takes the table for itself: any live room is left first.
      stopReplay();
      clientRef.current?.leave();
      clientRef.current = null;
      starts.current.reset();
      const player = new ReplayPlayer(game);
      replayRef.current = player;
      setReplay(player);
      setState({ phase: 'replay', summary: game.summary });
      onStartedRef.current({
        link: player,
        seat: SPECTATOR_SEAT,
        started: {
          t: 'started',
          seed: game.seed,
          setup: game.summary.setup,
          seats: game.summary.seats,
          names: game.summary.names,
        },
        history: [],
        onSession: (session) => player.attach(session),
      });
    },
    [stopReplay],
  );

  // A reloaded page finds its seat again while the server still keeps it.
  useEffect(() => {
    if (!serverUrl || !RoomClient.stored()) return;
    setState({ phase: 'busy', action: 'resume' });
    client()
      .resume()
      .then((room) => {
        if (!room.lobby.started) lobbyFound(room);
        // Otherwise `started` and `sync` follow and put the game on the table.
      })
      // A seat gone for good says so ("Your seat was handed to the AI"). Any other failure (a
      // server still waking, a Wi-Fi blip) keeps looking: the client retries with the seat's
      // token, and the lobby or the game comes back when it gets through.
      .catch((err: unknown) => {
        if (isFatal(err)) setState({ phase: 'idle', error: err.message });
      });
  }, [serverUrl, client, lobbyFound]);

  useEffect(
    () => () => {
      clientRef.current?.close();
      replayRef.current?.dispose();
    },
    [],
  );

  return { state, create, join, start, leave, listReplays, fetchReplay, watchReplay, replay };
}
